import { ReactNode, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { auth } from '../services/firebase';
import { User } from 'firebase/auth';

interface ProtectedRouteProps {
    children: ReactNode;
    requiredRole?: 'super_admin' | 'ops_manager' | 'support_agent';
}

type AdminRole = 'super_admin' | 'ops_manager' | 'support_agent' | null;

/**
 * ProtectedRoute component that checks:
 * 1. User is authenticated
 * 2. User has admin role in custom claims
 * 3. User meets minimum required role (if specified)
 */
const ProtectedRoute = ({ children, requiredRole }: ProtectedRouteProps) => {
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState<User | null>(null);
    const [adminRole, setAdminRole] = useState<AdminRole>(null);
    const [authorized, setAuthorized] = useState(false);

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
            setUser(currentUser);

            if (!currentUser) {
                setLoading(false);
                setAuthorized(false);
                return;
            }

            try {
                // Get ID token result to check custom claims
                const idTokenResult = await currentUser.getIdTokenResult();
                const role = idTokenResult.claims.adminRole as AdminRole;
                setAdminRole(role);

                // Check if user has an admin role
                if (!role) {
                    console.warn('User has no admin role:', currentUser.email);
                    setAuthorized(false);
                    setLoading(false);
                    return;
                }

                // Check if user meets required role level
                if (requiredRole) {
                    const authorized = checkRoleAuthorization(role, requiredRole);
                    setAuthorized(authorized);
                } else {
                    // If no specific role required, any admin role is sufficient
                    setAuthorized(true);
                }
            } catch (error) {
                console.error('Error checking admin role:', error);
                setAuthorized(false);
            }

            setLoading(false);
        });

        return () => unsubscribe();
    }, [requiredRole]);

    // Show loading spinner while checking auth
    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600 mx-auto mb-4"></div>
                    <p className="text-gray-600 text-lg">Verifying access...</p>
                </div>
            </div>
        );
    }

    // Redirect to login if not authenticated
    if (!user) {
        return <Navigate to="/" replace />;
    }

    // Show access denied if not authorized
    if (!authorized) {
        // Determine allowed roles based on required role (role hierarchy)
        const getAllowedRoles = (requiredRole: string): string[] => {
            const roleHierarchy = ['super_admin', 'ops_manager', 'support_agent'];
            const requiredIndex = roleHierarchy.indexOf(requiredRole);
            return requiredIndex !== -1 ? roleHierarchy.slice(0, requiredIndex + 1) : [];
        };

        const allowedRoles = requiredRole ? getAllowedRoles(requiredRole) : [];

        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
                    <div className="text-center">
                        <div className="text-6xl mb-4">🚫</div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
                        <p className="text-gray-600 mb-4">
                            {adminRole
                                ? `Your role (${adminRole}) does not have permission to access this resource.`
                                : 'You do not have admin privileges.'}
                        </p>
                        {allowedRoles.length > 0 && (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                                <p className="text-sm text-gray-700 mb-2">
                                    <strong>Access requires one of the following roles:</strong>
                                </p>
                                <p className="text-sm text-indigo-600 font-medium">
                                    {allowedRoles.join(', ')}
                                </p>
                            </div>
                        )}
                        <button
                            onClick={() => auth.signOut()}
                            className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 transition"
                        >
                            Sign Out
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // User is authenticated and authorized
    return <>{children}</>;
};

/**
 * Check if user's role meets the required role level
 * Role hierarchy: super_admin > ops_manager > support_agent
 */
function checkRoleAuthorization(userRole: AdminRole, requiredRole: string): boolean {
    if (!userRole) return false;

    const roleHierarchy: Record<string, number> = {
        'super_admin': 3,
        'ops_manager': 2,
        'support_agent': 1,
    };

    const userLevel = roleHierarchy[userRole] || 0;
    const requiredLevel = roleHierarchy[requiredRole] || 0;

    return userLevel >= requiredLevel;
}

export default ProtectedRoute;
